import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { CheckCircle2, Info } from 'lucide-react';

const examples = [
  {
    quote: "Étudiante en alternance, je découvre que je peux peut-être prétendre à l'APL et à la prime d'activité — deux aides souvent oubliées.",
    name: "Léa",
    age: "23 ans",
    role: "Étudiante en alternance",
    result: "Aides souvent oubliées",
  },
  {
    quote: "Salarié au SMIC, je vérifie en deux minutes mon éligibilité au chèque énergie et à la Complémentaire Santé Solidaire.",
    name: "Maxime",
    age: "26 ans",
    role: "Jeune salarié",
    result: "Vérification rapide",
  },
  {
    quote: "Avec les dates limites fiscales et un rappel quelques jours avant, je ne risque plus de rater ma déclaration ou ma demande APL.",
    name: "Camille",
    age: "21 ans",
    role: "Alternante",
    result: "Plus de date manquée",
  },
];

export const SocialProofSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="social-proof"
      className={`section-apple px-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}
      data-testid="social-proof-section"
    >
      <div className="max-w-[980px] mx-auto">
        {/* Headline */}
        <motion.div
          ref={ref}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="text-[13px] tracking-[0.02em] uppercase mb-4 text-[#81d8d0]">
            Exemples de situations
          </p>
          <h2
            className={`text-[40px] sm:text-[48px] font-semibold tracking-[-0.03em] leading-[1.1] mb-4 ${
              isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
            }`}
          >
            Trois cas fréquents.
            <br />
            <span className="text-[#81d8d0]">À toi de tester le tien.</span>
          </h2>
          <p
            className={`text-[14px] mt-3 max-w-[520px] mx-auto ${
              isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
            }`}
          >
            Les profils ci-dessous sont des exemples fictifs basés sur des cas
            fréquents rencontrés par les 18-30 ans en France.
          </p>
        </motion.div>

        {/* Reframed objectives banner */}
        <motion.div
          className={`rounded-2xl p-6 mb-12 text-center ${
            isDark ? 'bg-[#141414]' : 'bg-[#f5f5f7]'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-[32px] sm:text-[38px] font-semibold tracking-[-0.02em] text-[#81d8d0]">
                Jusqu'à 1 850€
              </p>
              <p
                className={`text-[13px] tracking-[-0.01em] ${
                  isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                }`}
              >
                d'aides parfois oubliées selon ta situation
              </p>
            </div>
            <div
              className={`hidden sm:block w-px h-12 ${
                isDark ? 'bg-[#3a3a3c]' : 'bg-[#d2d2d7]'
              }`}
            />
            <div className="text-center">
              <p
                className={`text-[32px] sm:text-[38px] font-semibold tracking-[-0.02em] ${
                  isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                }`}
              >
                Objectif : 0 paperasse
              </p>
              <p
                className={`text-[13px] tracking-[-0.01em] ${
                  isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                }`}
              >
                économise des heures sur tes démarches
              </p>
            </div>
            <div
              className={`hidden sm:block w-px h-12 ${
                isDark ? 'bg-[#3a3a3c]' : 'bg-[#d2d2d7]'
              }`}
            />
            <div className="text-center">
              <p className="text-[32px] sm:text-[38px] font-semibold tracking-[-0.02em] text-[#81d8d0]">
                Bêta ouverte
              </p>
              <p
                className={`text-[13px] tracking-[-0.01em] ${
                  isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                }`}
              >
                inscris-toi pour tester en avant-première
              </p>
            </div>
          </div>
        </motion.div>

        {/* Examples */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {examples.map((ex, index) => (
            <motion.div
              key={index}
              className={`p-6 rounded-2xl relative ${
                isDark ? 'bg-[#141414]' : 'bg-[#f5f5f7]'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              data-testid={`example-${index}`}
            >
              {/* Tag */}
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium mb-4 ${
                  isDark
                    ? 'bg-[#81d8d0]/10 text-[#81d8d0]'
                    : 'bg-[#81d8d0]/20 text-[#1d1d1f]'
                }`}
              >
                <CheckCircle2 size={12} />
                {ex.result}
              </div>

              {/* Quote */}
              <p
                className={`text-[15px] sm:text-[17px] leading-[1.5] tracking-[-0.01em] mb-4 ${
                  isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                }`}
              >
                "{ex.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-medium ${
                    isDark
                      ? 'bg-[#3a3a3c] text-[#f5f5f7]'
                      : 'bg-[#d2d2d7] text-[#1d1d1f]'
                  }`}
                >
                  {ex.name[0]}
                </div>
                <div className="flex-1">
                  <p
                    className={`text-[13px] font-medium ${
                      isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                    }`}
                  >
                    {ex.name}, {ex.age}
                  </p>
                  <p
                    className={`text-[11px] ${
                      isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
                    }`}
                  >
                    {ex.role}
                  </p>
                </div>
              </div>

              {/* Fictional badge */}
              <div
                className={`absolute top-3 right-3 flex items-center gap-1 text-[10px] uppercase tracking-widest ${
                  isDark ? 'text-[#3a3a3c]' : 'text-[#a1a1a6]'
                }`}
                title="Exemple fictif"
              >
                <Info size={10} />
                fictif
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
